import { useMemo } from 'react';

import { CoursePerformanceType } from '../../../../types/schema';
import { useFetchContentPerformancesQuery } from '../../graphql/queries/generated/contentPerformance';
import { useContentPerformanceSubscription } from '../../graphql/subscription/generated/contentPerformance';
import { CoursePerformanceFragment } from '../../modules/course/graphql/fragments/generated/CoursePerformance';

/**
 * @deprecated This hook is deprecated due to complex optional parameters.
 * Please use `useContentPerformanceV2`, which provides a more manageable API.
 *
 * **Migration Guide:**
 * Instead of:
 * ```tsx
 * useContentPerformance(entityIds, userId, includeLockSettings, pause, isLocalizedContent);
 * ```
 * Use:
 * ```tsx
 * useContentPerformanceV2({ entityIds, userId, includeLockSettings, pause, isLocalizedContent });
 * ```
 */

const useContentPerformance = (
  entityIds: string[],
  userId: string,
  includeLockSettings = true,
  pause: boolean = false,
  isLocalizedContent: boolean = false,
) => {
  const [{ fetching, data, error }, refetch] = useFetchContentPerformancesQuery({
    variables: {
      filters: { entityIds, userIds: [userId], isLocalizedContent },
      first: entityIds.length,
      includeLockSettings,
    },
    pause: !entityIds.length || pause,
    requestPolicy: 'cache-and-network',
  });

  useContentPerformanceSubscription({
    variables: { userId, entityIds, includeLockSettings },
    pause: !entityIds.length || !userId || pause,
  });

  const contentPerformance = useMemo(
    () =>
      data?.contentPerformances.edges?.reduce(
        (acc, edge) => {
          const node = edge.node as (typeof acc)['0'];
          if (!node?.entityId) {
            return acc;
          }

          acc[node.entityId] = node;
          return acc;
        },
        {} as Record<
          string,
          CoursePerformanceFragment & { lockSettings?: CoursePerformanceType['lockSettings'] }
        >,
      ),
    [data?.contentPerformances.edges],
  );

  return { fetching, error, data: contentPerformance, refetchContentPerformance: refetch };
};

export default useContentPerformance;
